import { currentTriple, encodeTriple } from "../sdk";
import { meshEnabled } from "./mesh-config";

// About page — a hash route (#about) so the site stays a single static document with no
// router dependency. Reads as a short report on what the Playground is and why it's
// noteworthy: deterministic, cross-engine, independently-reproducible in-browser compute,
// provable by permalink. Shipped as an open tool; the project is archived.
//
// Rendered into a dedicated overlay container so the playground DOM (gallery, inputs,
// results) is never destroyed — toggling the hash route shows/hides it cleanly.

let aboutEl: HTMLElement | null = null;

/** Show the About overlay and hide the playground + any other overlay. #about route. */
export function renderAbout(): void {
  hidePlayground();
  for (const o of document.querySelectorAll<HTMLElement>("main.contribute")) o.style.display = "none";

  if (!aboutEl) {
    aboutEl = document.createElement("main");
    aboutEl.className = "container about";
    aboutEl.innerHTML = aboutHtml();
    document.body.appendChild(aboutEl);
    // Leaving the About route (to home or another overlay) hides this overlay; the target
    // route's own renderer reveals what it needs.
    window.addEventListener("hashchange", () => {
      if (location.hash !== "#about") {
        if (aboutEl) aboutEl.style.display = "none";
        if (location.hash !== "#contribute") showPlayground();
      }
    });
  }
  aboutEl.style.display = "";
  window.scrollTo(0, 0);
}

function hidePlayground(): void {
  const p = document.getElementById("playground");
  if (p) p.style.display = "none";
}
function showPlayground(): void {
  const p = document.getElementById("playground");
  if (p) p.style.display = "";
}

function aboutHtml(): string {
  const t = encodeTriple(currentTriple());
  const mesh = meshEnabled();
  // The mesh section + hero aside only appear when this deployment actually has a coordinator
  // configured (VITE_MESH_URL); otherwise the page honestly describes the static-only tool.
  const meshSection = mesh
    ? `
    <section class="about-block">
      <h2>The mesh</h2>
      <p>
        The compute boundary (the <span class="mono">Runner</span> interface) was built so a
        mesh backend could drop in without changing this UI — and it does. Toggle
        <em>Run on the mesh</em> and a job is fanned across whatever browsers are connected
        instead of running locally; open the <a href="#contribute">Contribute</a> page and
        your own tab donates spare cycles to other people's jobs, backed by a small
        coordinator service. Workers are anonymous and opt-in, with a CPU cap and a one-click
        stop. Because every chunk is deterministic, a meshed run reproduces the same result
        bits as a local one — the guarantee survives distribution.
      </p>
    </section>`
    : `
    <section class="about-block">
      <h2>The mesh</h2>
      <p>
        The compute boundary (the <span class="mono">Runner</span> interface) was built so a
        mesh backend can drop in without changing this UI: a coordinator can fan chunks across
        many visitors' tabs, and the playground ships with that client. This build just doesn't
        point at a coordinator, so runs stay local — but because every chunk is deterministic,
        a meshed run reproduces the same result bits as a local one.
      </p>
    </section>`;
  return `
    <a class="back-link" href="#">← Back to the playground</a>
    <h1>About LiteSolve</h1>

    <p class="lede">
      The LiteSolve Playground runs Monte Carlo simulations in your browser and gives every
      run a permalink. Open that link on another computer — a different operating system, a
      different browser, a different number of CPU cores — and it re-derives the
      <strong>same result, bit for bit</strong>.${
        mesh
          ? " There's no sign-in, and by default no backend (an optional mesh mode, below, can spread a run across other browsers)."
          : " There's no backend and no sign-in; it's a static site."
      }
    </p>

    <section class="about-block">
      <h2>Why that's unusual</h2>
      <p>
        "Reproducible" computation usually means one of two weaker things: trust a server to
        run it for you, or get the same answer only on the same machine. Bit-for-bit agreement
        <em>across different JavaScript engines and hardware</em>, with the result
        independently checkable by anyone holding the link, is rare — floating-point math and
        parallel execution normally conspire against it. Demonstrating that it can be done is
        the reason this exists.
      </p>
    </section>

    <section class="about-block">
      <h2>What it's for</h2>
      <p>
        Pick a simulation kernel — π estimation, an SIR epidemic model, an options pricer, an
        agritech yield model — or paste your own. Run it across your browser's Web Workers, get
        a result and its distribution, and share the permalink so others can verify the number
        themselves rather than take your word for it.
      </p>
    </section>

    <section class="about-block">
      <h2>Why it reproduces</h2>
      <ul>
        <li>A run's result depends on <span class="mono">(kernel, seed, N)</span> only — never
          on your core count, worker-pool size, or the order chunks finish in. Work is split
          into a fixed set of chunks determined by <span class="mono">(kernel, N)</span>, and
          partials are reduced in fixed chunk-index order.</li>
        <li>All randomness comes from an integer-only, engine-portable PRNG
          (<span class="mono">xoshiro128**</span>). Kernels never touch
          <span class="mono">Math.random</span>, <span class="mono">Date</span>,
          <span class="mono">performance</span>, or <span class="mono">crypto</span> — a
          determinism linter enforces it.</li>
        <li><strong>Cross-engine honesty:</strong> a kernel is marked
          <em>verifiable</em> only when it reproduces bit-for-bit across V8 / SpiderMonkey /
          JavaScriptCore. Pure-arithmetic kernels (π, SIR) qualify on arithmetic alone.
          Kernels using transcendentals (log/exp/sin/cos) qualify too, because those go
          through a pinned <span class="mono">fdlibm</span> math library compiled to
          WebAssembly: WASM's f64 arithmetic is fully IEEE-specified, so the same bytes give
          the same bits on every engine — unlike the browsers' native
          <span class="mono">Math.*</span>, which disagree by 1–8 last-bit units. A kernel is
          non-verifiable only if its author opts out; the result panel makes the
          cross-engine claim only for verifiable runs, never overclaiming.</li>
      </ul>
    </section>

    ${meshSection}

    <section class="about-block">
      <h2>Design history</h2>
      <p>
        The architecture decisions, design specs, and the determinism experiments that
        produced these guarantees live in a separate planning repository.
      </p>
    </section>

    <section class="about-block">
      <h2 class="archived-note">Archived project</h2>
      <p class="muted">
        This is an archived project, shipped as an open tool rather than a maintained product.
        Reproducibility substrate: <span class="mono">${t}</span>
        (SDK · math-shim · fdlibm version triple).
      </p>
    </section>
  `;
}
